/**
 * The renderer's environment boundary.
 *
 * The core of this package imports **nothing** environment-specific. It talks to a
 * `RenderEnvironment` that supplies canvases and decoded images, so the identical
 * `renderScene()` runs in:
 *
 *   - the user's browser   (the normal path; GPU-backed, ~6 ms per export)
 *   - the user's machine   (via MCP, for agents that want files locally)
 *   - the backend worker   (API-triggered jobs, CI)
 *
 * That is the whole reason this product has one renderer instead of two. Two
 * implementations always drift, and drift between "what the editor showed" and "what was
 * exported" is the single most expensive bug class in a screenshot tool. It is also why
 * nothing in the core may import `@napi-rs/canvas` or touch `document`: those live in the
 * `./node` and `./browser` adapters.
 *
 * A deliberate consequence: callers pass a canvas *factory*, so a batch render can reuse
 * one surface instead of allocating per image.
 */

import type { DeviceFrame } from '@keymaker/frames';
import type { DeviceLayer } from '@keymaker/schema';

/** The subset of Canvas2D the renderer uses. Both DOM and @napi-rs/canvas satisfy it. */
export type Ctx2D = CanvasRenderingContext2D;

/**
 * A decoded image, ready to be drawn.
 *
 * `handle` carries the adapter's own drawable — a Skia `Image` on Node, an `HTMLImageElement`
 * in the browser. It is opaque to the core, which never inspects it, and it is what lets a
 * finished canvas be drawn back into another canvas (the perspective warp does exactly that)
 * without the core knowing what a canvas is.
 */
export interface RenderImage {
  readonly width: number;
  readonly height: number;
  readonly handle: unknown;
}

/**
 * A drawable surface. Extends `RenderImage` because a canvas is itself drawable, which the
 * perspective warp depends on.
 */
export interface RenderCanvas extends RenderImage {
  readonly width: number;
  readonly height: number;
  readonly ctx: Ctx2D;
  /** Encode PNG bytes. */
  toPng(): Promise<Uint8Array>;
  /** Read back raw RGBA, for tests and pixel assertions. */
  toRgba(): Uint8Array;
  /**
   * Let the surface go, once its pixels have been read.
   *
   * A browser canvas that has been put in the document to render correctly (see the browser adapter) is
   * kept alive by the document, so a run that renders ten panels would hold ten surfaces of fifteen
   * megabytes with nothing left to use them for. Environments without a document do nothing here.
   */
  release?(): void;
}

export interface RenderEnvironment {
  /** A fresh surface. `alpha: false` requests an opaque surface. */
  createCanvas(width: number, height: number, opts?: { alpha?: boolean }): RenderCanvas;

  /**
   * Decode an image. `src` is whatever the caller's asset resolver understands: a file
   * path in Node, a URL or object URL in the browser, or a key into an in-memory map.
   */
  loadImage(src: string): Promise<RenderImage>;

  /**
   * Register a font from bytes so browser and Node render text identically. Optional:
   * environments with the family already available can no-op.
   *
   * Text parity across environments is only guaranteed for fonts registered this way.
   * Falling back to system fonts is allowed but the resulting pixels may differ, which is
   * why the renderer records which fonts it resolved in `RenderResult.fontsResolved`.
   */
  registerFont?(family: string, bytes: Uint8Array): void;

  /** Draws a RenderImage; adapters know their own drawable type. */
  drawImage(ctx: Ctx2D, image: RenderImage, ...args: DrawImageArgs): void;

  /**
   * Optional Tier 3: draw a device body with WebGL. Absent means `device.kind: "3d"` is simply
   * unavailable in this environment, which is the honest arrangement — a server without a GPU
   * still renders every Tier 1 and Tier 2 template at full quality.
   *
   * Asynchronous by nature, which is why it is an explicit pre-pass rather than part of
   * `renderScene`: the compositing path stays synchronous, so a 500-panel batch is not 500
   * promises deep and the hot path costs nothing for a feature most projects never use.
   */
  render3d?(request: DeviceRenderRequest): Promise<RenderImage>;
}

export type DrawImageArgs =
  /** drawImage(image, dx, dy) */
  | [number, number]
  /** drawImage(image, dx, dy, dw, dh) */
  | [number, number, number, number]
  /** drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh) — used by the perspective warp's per-cell draw. */
  | [number, number, number, number, number, number, number, number];

export interface RenderOptions {
  /**
   * Assets the scene references, resolved by key. A device layer's `screens` entries and
   * an image layer's `src` are looked up here before hitting `env.loadImage`.
   */
  assets?: ReadonlyMap<string, RenderImage>;
  /** Called when a referenced asset is missing, instead of throwing mid-render. */
  onMissingAsset?: (ref: string, where: string) => void;
  /** Cap on the auto-shrink search for text layers. */
  maxShrinkSteps?: number;
  /** Flatten onto this colour; null keeps transparency (only valid for previews). */
  flatten?: string | null;
  /**
   * Pre-rendered Tier 3 device bodies, keyed by device layer id.
   *
   * Produced by `prepare3dDevices()`. A `kind: "3d"` layer with no entry here is an error rather
   * than a silently empty device, because a blank panel is far harder to notice than a message.
   */
  devices?: ReadonlyMap<string, RenderImage>;
}

/**
 * A request to draw a device body in 3D.
 *
 * Deliberately minimal: the frame supplies every measurement — body proportions, display
 * rectangle, corner radius — so a 3D build needs the user's screenshot and nothing else. That is
 * also why switching tiers is a one-field change in a scene document.
 */
export interface DeviceRenderRequest {
  frame: DeviceFrame;
  screen: RenderImage;
  /** Pixel size to render at. The frame canvas size keeps placement identical to Tier 1. */
  width: number;
  height: number;
  styling?: DeviceLayer['body'];
  /** Degrees. Positive x tips the top away, matching the Tier 2 convention. */
  rotation?: { x?: number; y?: number; z?: number };
  zoom?: number;
  /**
   * Which device this is, e.g. a layer id.
   *
   * An adapter is allowed to reuse its output buffer for a given key, because the image is consumed
   * inside the render that produced it. Two devices in one scene must not share a key, or the second
   * would overwrite the first before it was composited.
   */
  key?: string;
}

export class RenderError extends Error {
  constructor(
    message: string,
    readonly code:
      | 'unknown-frame'
      | 'unknown-variant'
      | 'missing-asset'
      | 'bad-canvas'
      | 'opaque-required'
      | '3d-unavailable',
  ) {
    super(message);
    this.name = 'RenderError';
  }
}
